﻿import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { getFirebaseWebFirestore } from "./firebaseWeb";
import { getLiveActionResource, postLiveAction } from "./liveActionApi";
import { buildDefaultEventCreateProfile, validateEventCreatePolicy } from "./userPolicyRepository";

const WEB_EVENTS_COLLECTION = "webEvents";

function cleanText(value) {
  return String(value || "").trim();
}

function normalizeTags(tags) {
  if (!Array.isArray(tags)) return [];
  return tags.map((tag) => cleanText(tag)).filter(Boolean).slice(0, 20);
}

function normalizeLocation(location) {
  const lat = Number(location?.lat);
  const lng = Number(location?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return null;
  return { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) };
}

function buildCreatePayload({ selectedIdea, draft, user, account }) {
  const safeTitle = cleanText(draft?.title || selectedIdea?.title);
  if (!safeTitle) throw new Error("Etkinlik başlığı boş olamaz.");

  const tunable = draft?.tunable || {};
  const createProfile = buildDefaultEventCreateProfile(account?.eventCreateProfile);

  return {
    source: "denkup-com",
    status: "active",
    title: safeTitle,
    eventIdeaId: selectedIdea?.id || null,
    category: selectedIdea?.category || "Custom",
    subCategory: selectedIdea?.subCategory || "Custom",
    groupSize: tunable.groupSize || selectedIdea?.groupSize || "Squad",
    durationType: draft?.durationType || selectedIdea?.durationType || "TwoHours",
    timeOfDay: selectedIdea?.timeOfDay || [],
    seasonality: selectedIdea?.seasonality || ["AllYear"],
    locationType: selectedIdea?.locationType || "City",
    indoorOutdoor: tunable.indoorOutdoor || selectedIdea?.indoorOutdoor || "Both",
    rankLevel: tunable.rankLevel || selectedIdea?.rankLevel || "Free",
    energy: tunable.energy || selectedIdea?.energy || "Social",
    planningDiff: tunable.planningDiff || selectedIdea?.planningDiff || "Easy",
    audience: selectedIdea?.audience || [],
    purpose: selectedIdea?.purpose || [],
    trendLevel: tunable.trendLevel || selectedIdea?.trendLevel || "Classic",
    minRequiredTrustScore: Number(draft?.minRequiredTrustScore || 0),
    attendanceRequiredToCreate: Number(selectedIdea?.attendanceRequiredToCreate || draft?.attendanceRequiredToCreate || 0),
    moderationStatus: createProfile.requiresModeratorReview ? "ai_approved_pending_moderator" : "auto_approved",
    tags: normalizeTags(selectedIdea?.tags),
    selectedPreset: Boolean(selectedIdea?.id),
    editable: {
      date: cleanText(draft?.date),
      time: cleanText(draft?.time),
      minParticipants: Number(draft?.minParticipants || 2),
      maxParticipants: Number(draft?.maxParticipants || 2),
      venueName: cleanText(draft?.venueName),
      location: normalizeLocation(draft?.location),
      locationAddress: cleanText(draft?.locationAddress),
      addressLine1: cleanText(draft?.addressLine1),
      addressDistrict: cleanText(draft?.addressDistrict),
      addressCity: cleanText(draft?.addressCity),
      addressCountry: cleanText(draft?.addressCountry),
      visibility: cleanText(draft?.visibility || "Public"),
      seriesType: cleanText(draft?.seriesType || "OneTime"),
      presetKey: cleanText(draft?.presetKey || "balanced"),
      rewardsEnabled: Boolean(draft?.rewardsEnabled),
      approvalRequired: Boolean(draft?.approvalRequired),
      showSeatMap: Boolean(draft?.showSeatMap),
      confirmationPolicy: cleanText(draft?.confirmationPolicy || "Confirm2HoursBefore"),
      cancellationLock: cleanText(draft?.cancellationLock || "NoCancel30MinutesBefore"),
      categorySettings: draft?.categorySettings || {},
      tunable,
      minRequiredTrustScore: Number(draft?.minRequiredTrustScore || 0),
    },
    createPolicy: {
      requiresModeratorReview: Boolean(createProfile.requiresModeratorReview),
      requiredLegalKeys: createProfile.requiredLegalKeys,
      lastMinuteJoinEnabled: account?.participationEligibility?.lastMinuteJoinEnabled !== false,
      verifiedPublicSupported: createProfile.allowedVisibilityTypes.includes("VerifiedPublic"),
    },
    host: {
      uid: user?.uid || null,
      displayName: user?.displayName || null,
      nickname: user?.nickname || null,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

function mapDocumentToEvent(docSnapshot) {
  const data = docSnapshot.data() || {};
  const editable = data.editable || {};

  return {
    id: docSnapshot.id,
    status: data.status || "active",
    title: data.title || "",
    category: data.category || "Custom",
    subCategory: data.subCategory || "Custom",
    durationType: data.durationType || "TwoHours",
    timeOfDay: Array.isArray(data.timeOfDay) ? data.timeOfDay : [],
    locationType: data.locationType || "City",
    indoorOutdoor: data.indoorOutdoor || "Both",
    groupSize: data.groupSize || "Squad",
    energy: data.energy || "Social",
    planningDiff: data.planningDiff || "Easy",
    trendLevel: data.trendLevel || "Classic",
    rankLevel: data.rankLevel || "Free",
    seasonality: Array.isArray(data.seasonality) ? data.seasonality : ["AllYear"],
    minRequiredTrustScore: Number(data.minRequiredTrustScore || editable.minRequiredTrustScore || 0),
    moderationStatus: data.moderationStatus || "auto_approved",
    participantSummary: data.participantSummary || null,
    participantUids: data.participantUids || null,
    highlights: Array.isArray(data.highlights) ? data.highlights : [],
    isTestEvent: Boolean(data.isTestEvent || data.testEvent),
    host: data.host || null,
    createdAt: data.createdAt?.toDate?.() || null,
    editable: {
      date: editable.date || "",
      time: editable.time || "",
      minParticipants: editable.minParticipants || 2,
      maxParticipants: editable.maxParticipants || 2,
      venueName: editable.venueName || "",
      locationAddress: editable.locationAddress || "",
      addressLine1: editable.addressLine1 || "",
      addressDistrict: editable.addressDistrict || "",
      addressCity: editable.addressCity || "",
      addressCountry: editable.addressCountry || "",
      location: editable.location || null,
      visibility: editable.visibility || "Public",
      seriesType: editable.seriesType || "OneTime",
      presetKey: editable.presetKey || "balanced",
      rewardsEnabled: Boolean(editable.rewardsEnabled),
      approvalRequired: Boolean(editable.approvalRequired),
      showSeatMap: Boolean(editable.showSeatMap),
      confirmationPolicy: editable.confirmationPolicy || "Confirm2HoursBefore",
      cancellationLock: editable.cancellationLock || "NoCancel30MinutesBefore",
      tunable: editable.tunable || {},
    },
  };
}

export async function createWebEventDraft({ selectedIdea, draft, user, account, allLegalAccepted = true }) {
  const db = getFirebaseWebFirestore();
  if (!user?.uid) throw new Error("Etkinlik oluşturmak için giriş yapmalısın.");

  const existingEvents = await listUserWebEventDrafts({ uid: user.uid, max: 120 });
  const policy = validateEventCreatePolicy({ account, draft, selectedIdea, existingEvents, legalAccepted: allLegalAccepted });
  if (!policy.allowed) {
    const error = new Error(policy.message);
    error.field = policy.field;
    error.code = "policy-denied";
    throw error;
  }

  const payload = buildCreatePayload({ selectedIdea, draft, user, account });
  try {
    const docRef = await addDoc(collection(db, WEB_EVENTS_COLLECTION), payload);
    return { id: docRef.id, ...payload };
  } catch (error) {
    const result = await postLiveAction("createEvent", { payload });
    return { id: result.id, ...payload };
  }
}

export async function listRecentWebEventDrafts({ max = 24 } = {}) {
  let db;
  try {
    db = getFirebaseWebFirestore();
  } catch {
    return getLiveActionResource("events");
  }

  const eventQuery = query(
    collection(db, WEB_EVENTS_COLLECTION),
    orderBy("createdAt", "desc"),
    limit(Math.max(1, Math.min(max, 500))),
  );

  try {
    const snapshot = await getDocs(eventQuery);
    return snapshot.docs.map(mapDocumentToEvent);
  } catch {
    return getLiveActionResource("events");
  }
}

export async function listUserWebEventDrafts({ uid, max = 40 } = {}) {
  if (!uid) return [];
  const all = await listRecentWebEventDrafts({ max });
  return all.filter((event) => event.host?.uid === uid);
}

export async function listUserPublicEventActivity({ uid, max = 80 } = {}) {
  if (!uid) return [];
  const all = await listRecentWebEventDrafts({ max });
  return all
    .filter((event) => {
      const participantUids = event.participantUids || {};
      const participantBuckets = [
        ...(Array.isArray(participantUids.confirmed) ? participantUids.confirmed : []),
        ...(Array.isArray(participantUids.maybe) ? participantUids.maybe : []),
        ...(Array.isArray(participantUids.pending) ? participantUids.pending : []),
      ];
      return event.host?.uid === uid || participantBuckets.includes(uid);
    })
    .slice(0, Math.max(1, Math.min(Number(max) || 80, 200)));
}
